<template>
    <fieldset 
        :class="[
            'd-flex',
            'flex-column',
            'my-6',
            'flex-grow-1',
            borderWidthCssClass,
            borderColorCssClass,
            borderStyleCssClass,
        ]"
    >
        <legend :class="`${props.fieldsetIndicatorLabelTextCssClass} ms-4 mb-4`">
            <span class="ms-2"></span>
            <span>{{ props.indicatorLabel }}</span>
            <span class="ms-2"></span>
        </legend>

        <fieldset 
            :class="[
                'ms-4',
                borderWidthCssClass,
                borderColorCssClass,
                borderStyleCssClass,
            ]"
        >
            <legend :class="`${props.translationsLabelTextCssClass} ms-4 mb-4`">
                <span class="ms-2"></span>
                <span>{{ props.translationsLabel }}</span>
                <span class="ms-2"></span>
            </legend>
            <v-row 
                v-for="(localized, index) in formValues.localizations"
                :key="localized?.id ?? localized?.clientKey ?? index"
                class="ma-0 ms-4 ga-4 flex-grow-1"
            >
                <v-col cols="12" md="3" class="pa-0">
                    <v-autocomplete
                        v-model="localized.locale_id"
                        label="Língua / Localização"
                        :items="getAvailableIndicatorLocalizations(localized.locale_id)"
                        :item-title="(item) => `${item.name} (${item.code})`"
                        item-value="id"
                        :readonly="readonlyFields"
                        :clearable="!readonlyFields"
                        :menu-icon="readonlyFields ? '' : undefined"
                        autocomplete="off"
                        :disabled="
                            props.disabled
                            || props.submitting
                            || props.loadingLocales
                            || props.isLoadingIndicators
                            || isSubmitting
                            || isLoadingLocales
                        "
                        :loading="
                            props.loadingLocales
                            || props.isLoadingIndicators
                            || isLoadingLocales
                        "
                        :rules="[
                            formValidationRules.required(localized.locale_id)
                        ]"
                    />
                </v-col>
                <v-row class="row ma-0 d-flex justify-end">
                    <v-col cols="12" class="pa-0">
                        <v-text-field
                            v-if="indicatorIsTechnical"
                            v-model="localized.name"
                            label="Nome"
                            :readonly="readonlyFields"
                            :clearable="!readonlyFields"
                            :disabled="
                                props.disabled
                                || props.submitting
                                || props.loadingLocales
                                || props.isLoadingIndicators
                                || isSubmitting
                                || isLoadingLocales
                            "
                            :loading="props.isLoadingIndicators"
                            :rules="[
                                formValidationRules.required(localized.name),
                            ]"
                        />
                        <v-text-field
                            v-else
                            v-model="localized.description"
                            label="Descrição"
                            :readonly="readonlyFields"
                            :clearable="!readonlyFields"
                            :disabled="
                                props.disabled
                                || props.submitting
                                || props.loadingLocales
                                || props.isLoadingIndicators
                                || isSubmitting
                                || isLoadingLocales
                            "
                            :loading="props.isLoadingIndicators"
                            :rules="[
                                formValidationRules.required(localized.description),
                            ]"
                        />
                    </v-col>
                    <v-col cols="auto" class="px-0 mb-8">
                        <v-btn
                            v-if="formValues.localizations?.length > 1 && !props.readonly"
                            prepend-icon="mdi-delete"
                            color="error"
                            variant="outlined"
                            class="text-none"
                            :disabled="props.submitting || isSubmitting || props.isLoadingIndicators"
                            @click="formValues.localizations.splice(index, 1)"
                        >
                            Remover
                        </v-btn>
                    </v-col>
                </v-row>
                <hr v-if="formValues.localizations?.length > 1">
            </v-row>
            <v-row class="mx-auto mt-8 mb-4 flex-column justify-center align-center ga-4">
                <v-btn 
                    v-if="!props.readonly"
                    class="text-none" 
                    color="success" 
                    variant="outlined"
                    :disabled="props.submitting || isSubmitting || props.isLoadingIndicators"
                    @click="formValues.localizations.push({...indicatorLocalizationTemplate})"
                >
                    <template #prepend>
                        <v-icon>mdi-plus</v-icon>
                        <v-icon>mdi-translate</v-icon>
                    </template>
                    Traduzir {{props.indicatorLabel}}
                </v-btn>

                <v-btn 
                    v-if="props.showDeleteIndicatorButton && !props.readonly"
                    class=" text-none" 
                    color="error" 
                    variant="flat"
                    prepend-icon="mdi-delete"
                    :disabled="props.submitting || isSubmitting || props.isLoadingIndicators"
                    @click="$emit('removeIndicator')"
                >
                    Apagar {{props.indicatorLabel}}
                </v-btn>
            </v-row>
        </fieldset>
    </fieldset>

</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import Indicators from "@/api/assessments/competencies/Indicators";
import Locales from "@/api/locales/Locales";
import formValidationRules from '../../../composables/formValidationRules';

const props = defineProps({
    indicatorType: {
        type: String,
        required: true
    },
    indicator: {
        type: Object,
        default: null,
    },
    indicatorId: {
        type: String,
        default: null,
    },
    action: {
        type: String,
        default: "create",
    },
    onSubmit: {
        type: Function,
        default: null,
    },
    indicatorLabel: {
        type: String,
        default: 'Indicador',
    },
    localesList : {
        type: Array,
        default: undefined
    },
    borderWidthCssClass : {
        type: String,
        default: 'border-0',
    },
    borderColorCssClass: {
        type: String,
        default: 'border-primary',
    },
    borderStyleCssClass : {
        type: String,
        default: 'border-solid'
    },
    fieldsetIndicatorLabelTextCssClass: {
        type: String,
        default: 'text-h5',
    },
    translationsLabel: {
        type: String,
        default: 'Traduções'
    },
    translationsLabelTextCssClass: {
        type: String,
        default: 'text-h6',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    submitting: {
        type: Boolean,
        default: false,
    },
    loadingLocales : {
        type: Boolean,
        default: false,
    },
    showDeleteIndicatorButton: {
        type: Boolean,
        default: false,
    },
    isLoadingIndicators : {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "removeIndicator",
    "update:indicator",
]);

defineExpose({
    submit,
});

const readonlyFields = ref(props.readonly ?? false);
const isSubmitting = ref(false)

const indicatorLocalizationTemplate = {
    locale_id: null,
    name: null,
    description: null,
};

const formValues = ref(
    props.indicator
    ? normalizeIndicator(props.indicator)
    : {
    id: props.indicatorId ?? null,
    localizations : [
        {...indicatorLocalizationTemplate},
    ],
}
);

watch(
    formValues,
    (indicator) => {
        emit("update:indicator", normalizeIndicator(indicator));
    },
    {
        deep: true,
    },
);

const indicatorIsTechnical = computed(() => {
    return props.indicatorType === "technical";
});

const standaloneLocalesList = ref([]);
const isLoadingStandaloneLocales = ref(false);

const usesProvidedLocalesList = computed(() => {
    return props.localesList !== undefined;
});

const isLoadingLocales = computed(() => {
    return usesProvidedLocalesList.value
        ? props.loadingLocales
        : isLoadingStandaloneLocales.value;
});

const effectiveLocalesList = computed(() => {
    return usesProvidedLocalesList.value
        ? props.localesList
        : standaloneLocalesList.value;
});

const getAvailableIndicatorLocalizations = (currentLocaleId) => {
    const selectedLocaleIds = new Set(
        formValues.value.localizations
            .map((localized) => localized.locale_id)
            .filter(Boolean),
    );
    

    return effectiveLocalesList.value?.filter((locale) => {
        return (
            locale.id === currentLocaleId
            || !selectedLocaleIds.has(locale.id)
        );
    });
};

function normalizeIndicator(indicator) {
    const localizations = indicator.localizations
        ?? indicator.translations
        ?? [];

    return {
        ...indicator,
        localizations: localizations.map((localization) => ({
            locale_id: localization.locale_id,
            name: localization.name ?? null,
            description: localization.description ?? null,
        })),
    };
}

async function submit() {
    const payload = {
        number: formValues.value.number ?? null,
        localizations: formValues.value.localizations,
    };

    if (props.onSubmit) {
        return props.onSubmit(payload);
    }

    switch (props.action) {
        case "create":
        case "new":
            if (!props.competencyId) {
                throw new TypeError("A competencyId is required to create an indicator.");
            }

            return Indicators.create(props.indicatorType, {
                competency_id: props.competencyId,
                ...payload,
            });

        case "update":
        case "edit":
            if (!formValues.value.id) {
                throw new TypeError("An indicator id is required to update an indicator.");
            }

            return Indicators.update(
                props.indicatorType,
                formValues.value.id,
                payload,
            );

        case "delete":
            if (!formValues.value.id) {
                throw new TypeError("An indicator id is required to delete an indicator.");
            }

            return Indicators.delete(props.indicatorType, formValues.value.id);

        default:
            throw new RangeError(`Unsupported indicator action: ${props.action}.`);
    }
}


async function fetchLocalesList() {
    if (
        usesProvidedLocalesList.value
        || isLoadingStandaloneLocales.value
    ) {
        return;
    }

    isLoadingStandaloneLocales.value = true;

    try {
        const response = await Locales.all();

        standaloneLocalesList.value = Array.isArray(response.data)
            ? response.data
            : [];

    } catch (error) {

        console.error(error);

        throw error;

    } finally {

        isLoadingStandaloneLocales.value = false;

    }
}

onMounted(() => {
    if (!usesProvidedLocalesList.value) {

        fetchLocalesList();
    }
});
</script>
